import Swiper from "swiper";
import { Autoplay, Pagination, A11y, Navigation, Manipulation } from "swiper/modules";
import { breakpoint } from "@utils/breakpoints";

const styleSchemas = {
  light: "style-light",
  dark: "style-dark",
  color: "style-color",
};

const classNames = {
  carouselOptimize: "carousel-optimize",
  dotAnimatedBorder: "dot-animated-border",
  autoPlayPaused: "auto-play-paused",
  fullHeightExceeded: "full-height-exceeded",
  isAuthoring: "is-authoring",
};

const selectors = {
  swiperNavInnerContainer: ".swiper-navigation-inner-container",
  swiperNavContainer: ".swiper-navigation-container",
  swiperDots: ".swiper-dots",
  swiperNext: ".swiper-next",
  swiperPrev: ".swiper-prev",
  swiperBullet: ".swiper-bullet",
  carouselContainer: ".rotator-section-container",
  dotAnimatedBorder: ".dot-animated-border",
};

const attributes = {
  rotationSpeed: "data-rotation-speed",
  rotationMobileSpeed: "data-rotation-mobile-speed",
  autoPlay: "data-autorotate",
};

class RotatorSection {
  private el: HTMLElement;
  private rotationSpeed: number;
  private rotationMobileSpeed: number;
  private autoPlay: boolean;
  private currentSlideColorSchema: string;
  private isAuthoring: boolean;
  private navContainer: HTMLElement;
  private parent: HTMLElement;
  private swiper: Swiper;
  private swiperNextButton: HTMLElement;
  private swiperPrevButton: HTMLElement;
  private columnsWrapper: HTMLElement;
  private liveregion: HTMLElement;
  private dots: HTMLElement;
  private slides: NodeListOf<HTMLElement>;
  private stash: HTMLElement[];

  constructor(el: HTMLElement) {
    this.el = el;
    this.parent = this.el.parentNode as HTMLElement;
    this.isAuthoring = this.el.classList.contains(classNames.isAuthoring);
    this.navContainer = this.el.querySelector(selectors.swiperNavContainer);
    this.dots = this.el.querySelector(selectors.swiperDots);
    this.swiperPrevButton = this.el.querySelector(selectors.swiperPrev);
    this.swiperNextButton = this.el.querySelector(selectors.swiperNext);
    this.columnsWrapper = this.el.querySelector(selectors.carouselContainer);
    this.slides = this.el.querySelectorAll(`${selectors.carouselContainer} > .section`);
    this.slides.forEach(slide => slide.classList.add("swiper-slide"));
    this.createSwiperStash();
    this.rotationSpeed =
      Number(this.el.getAttribute(attributes.rotationSpeed)) || 5000;
    this.rotationMobileSpeed =
      Number(this.el.getAttribute(attributes.rotationMobileSpeed)) || 5000;
    const autoPlayData = this.el.getAttribute(attributes.autoPlay);
    this.autoPlay = autoPlayData === "true";

    this.currentSlideColorSchema = styleSchemas.light;

  }

  public init() {
    this.initSwiper();
  }

  private initSwiper = () => {
    if (this.isAuthoring) {
      // In authoring mode, we do not initialize swiper
      return;
    }

    this.swiper = new Swiper(this.el, {
      loop: true,
      roundLengths: true,
      allowTouchMove: true,
      modules: [Pagination, Autoplay, Navigation, A11y, Manipulation],
      speed: 300,
      autoplay: this.autoPlay ? {
        delay: this.rotationMobileSpeed,
      } : false,
      slidesPerView: 1,
      slidesPerGroup: 1,
      pagination: {
        el: this.dots,
        type: 'bullets',
        clickable: true,
        bulletClass: "swiper-bullet",
        bulletActiveClass: "swiper-bullet-active",
      },
      navigation: {
        nextEl: this.swiperNextButton,
        prevEl: this.swiperPrevButton,
        disabledClass: "swiper-disabled"
      },
      breakpoints: {
        // 768 added to be used by onBreakpointListener
        768: {},
        1025: {
          autoplay: this.autoPlay ? {
            delay: this.rotationSpeed,
          } : false,
        }
      },
      on: {
        beforeInit: (swiper) => {
          this.handleHidingSections(swiper);
        },
        slideChange: (swiper) => {
          this.setCurrentSlideColorSchema(swiper);
          this.toggleInertForNonActiveSlides(swiper);
        },

        afterInit: (swiper) => {
          this.setCurrentSlideColorSchema(swiper);
          this.handleArrowButtonListeners();
          this.initPauseListeners(swiper);
          this.announceCurrentSlide();
          this.toggleInertForNonActiveSlides(swiper);

          this.el.classList.remove(classNames.carouselOptimize);
        },

        beforeResize: () => {
          this.handleHeightExceedingFullHeight();
        },

        breakpoint: (swiper) => {
          this.handleHidingSections(swiper);
          this.expandBullets();
          this.toggleInertForNonActiveSlides(swiper);
        }
      }
    });
    this.handleHeightExceedingFullHeight();
    this.expandBullets();

    this.liveregion = this.createLiveRegion();

    this.removeIframesFromTabOrder();
  }

  private expandBullets = () => {
    const bullet = this.el.querySelectorAll(selectors.swiperBullet);

    bullet.forEach((bullet) => {
      const isExpanded =
        bullet.querySelectorAll(selectors.dotAnimatedBorder).length > 0;

      if (!isExpanded) {
        bullet.appendChild(this.createCircleElement());
      }
    });
  }

  private handleArrowButtonListeners = () => {
    this.swiperNextButton.addEventListener("click", () => this.pauseAutoPlay(true));
    this.swiperNextButton.addEventListener("focus", () => this.pauseAutoPlay());
    this.swiperNextButton.addEventListener("blur", () => this.restartAutoPlay());

    this.swiperPrevButton.addEventListener("click", () => this.pauseAutoPlay(true));
    this.swiperPrevButton.addEventListener("focus", () => this.pauseAutoPlay());
    this.swiperPrevButton.addEventListener("blur", () => this.restartAutoPlay());
  }

  private pauseAutoPlay = (permanent = false) => {
    if (permanent) {
      this.autoPlay = false;
    }

    this.swiper.autoplay.stop();

    this.el.classList.add(classNames.autoPlayPaused);
  }

  private createLiveRegion = () => {
    const liveregion = document.createElement("div");

    liveregion.setAttribute("aria-live", "polite");
    liveregion.setAttribute("aria-atomic", "true");
    liveregion.setAttribute("class", "liveregion");

    this.columnsWrapper.appendChild(liveregion);

    return liveregion;
  }

  private restartAutoPlay = () => {
    if (this.autoPlay === true) {
      this.swiper.autoplay.start();

      this.el.classList.remove(classNames.autoPlayPaused);
    }
  }

  private createCircleElement = () => {
    const circle = document.createElement("span");

    circle.classList.add(classNames.dotAnimatedBorder);
    circle.innerHTML = `<svg viewBox="0 0 24 24" width="24" height="24">
			<circle class="dot-animated-border-fill" cx="12" cy="12" r="11" />
		</svg>`;

    return circle;
  }

  private removeIframesFromTabOrder = () => {
    const targetNode = this.el;
    const config = { childList: true, subtree: true };

    const callback = (mutationList, observer) => {
      for (const mutation of mutationList) {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            if (node.tagName === "IFRAME") {
              node.setAttribute("tabindex", "-1");
            }
          });
        }
      }
    };

    const observer = new MutationObserver(callback);

    observer.observe(targetNode, config);
  }

  private setCurrentSlideColorSchema = (swiper: Swiper) => {
    let activeSlide = swiper.slides[swiper.activeIndex];

    const rootSectionOfActiveSlide = activeSlide?.querySelector(".comp-section");
    const colorPriority = [
      styleSchemas.light,
      styleSchemas.dark,
      styleSchemas.color,
    ];
    const tempColorSchemas = colorPriority.find((schema) =>
      rootSectionOfActiveSlide?.classList.contains(schema),
    );

    if (this.currentSlideColorSchema !== tempColorSchemas) {
      this.parent.classList.remove(this.currentSlideColorSchema);
      this.parent.classList.add(tempColorSchemas);
      this.currentSlideColorSchema = tempColorSchemas;
    }
  }

  private initPauseListeners = (swiper: Swiper) => {
    this.navContainer.addEventListener("mouseover", () => this.pauseAutoPlay());
    this.navContainer.addEventListener("mouseout", (e) => {
      const target = e.target as HTMLElement;
      const isInnerNavigation = !!target.closest(
        selectors.swiperNavInnerContainer,
      );

      if (!isInnerNavigation) {
        this.restartAutoPlay();
      }
    });

    this.navContainer.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      const isSwiperDots = !!target.closest(selectors.swiperDots);

      if (isSwiperDots) {
        this.pauseAutoPlay(true);
      }
    });

    this.slides.forEach((slide) => {
      slide.addEventListener("click", () => this.pauseAutoPlay(true));
    });

    this.columnsWrapper.addEventListener("mouseover", (e) => {
      const target = e.target as HTMLElement;
      const isLinkOrButton = !!target.closest("a, button");

      if (isLinkOrButton) {
        this.pauseAutoPlay();
      } else {
        this.restartAutoPlay();
      }
    });

    swiper.on("touchMove", () => this.pauseAutoPlay(true));

    const linksAndButtons = this.columnsWrapper.querySelectorAll("a, button");
    const dots = this.navContainer.querySelectorAll(selectors.swiperDots);

    linksAndButtons.forEach((linkOrButton) => {
      linkOrButton.addEventListener("focus", () => this.pauseAutoPlay());
      linkOrButton.addEventListener("blur", () => this.restartAutoPlay());
    })

    dots.forEach((dot) => {
      dot.addEventListener("focus", () => this.pauseAutoPlay());
      dot.addEventListener("blur", () => this.restartAutoPlay());
    })
  }

  private announceCurrentSlide = () => {
    if (this.liveregion && !this.autoPlay) {
      this.liveregion.textContent = `Slide ${this.swiper.activeIndex + 1} of ${this.swiper.slides.length}`;
    }
  }

  private toggleInertForNonActiveSlides = (swiper: Swiper) => {
    swiper.slides.forEach((slide, index) => {
      if (index === swiper.activeIndex) {
        slide.removeAttribute("inert");
      } else {
        slide.setAttribute("inert", "true");
      }
    });
  }

  private handleHidingSections = (swiper: Swiper) => {
    const getBreakpoint = () => {
      if (breakpoint.isSmDown()) {
        return "small";
      } else if (breakpoint.isMd()) {
        return "medium";
      } else if (breakpoint.isLgUp()) {
        return "large";
      } else {
        return null;
      }
    }

    const breakpointClass = getBreakpoint();

    const newSlides = this.stash.map(slide => {
      if (breakpointClass) {
        const shouldHide = slide.querySelector(`.comp-section.hide-${breakpointClass}`) !== null;

        if (!shouldHide) {
          return slide;
        } else {
          return null;
        }
      }
    })
    .filter(slide => slide);

    // Batch the DOM change; appendSlide already triggers a Swiper update, so the
    // extra update()/pagination.update()/navigation.update() calls were redundant
    // forced reflows. slideTo folds the reset into the same tick.
    swiper?.removeAllSlides();
    swiper?.appendSlide(newSlides);
    swiper?.slideTo(0, 0);
  }

  private createSwiperStash = () => {
    this.stash = [...this.slides]
  }

  private handleHeightExceedingFullHeight = () => {
    const fullScreenHeight = this.calculateFullScreenHeight();
    const sectionHeight = this.columnsWrapper.offsetHeight;

    const isExceeding = sectionHeight > fullScreenHeight;

    this.el.classList.toggle(classNames.fullHeightExceeded, isExceeding);
  }

  private calculateFullScreenHeight = () => {
    return window.innerHeight - this.calculateHeaderHeight();
  }

  private calculateHeaderHeight = () => {
    return document.querySelector<HTMLElement>(".comp-nav-bar")?.offsetHeight ?? 0;
  }
}


const components = {
  "rotator-section": RotatorSection
};

export function initComponents(root = document) {
  Object.entries(components).forEach(([name, Component]) => {
    root.querySelectorAll<HTMLElement>(`[data-component="${name}"]`).forEach(el => {
      new Component(el).init();
    });
  });
}

// if (document.readyState !== "loading") {
//   initComponents();
// } else {
//   document.addEventListener("DOMContentLoaded", () => initComponents());
// }
